import {
  all,
  call,
  put,
  select,
  takeLatest,
  throttle,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import api, { addressApi } from 'api';
import {
  actions as warningActions,
  selectors as warningsSelectors,
} from 'state/warnings';
import {
  actions as errorActions,
  selectors as errorsSelectors,
} from 'state/errors';
import { selectors as settingsSelectors } from 'state/settings';
import { actions as uiActions } from 'state/ui';
import {
  actions as shipmentActions,
  selectors as shipmentSelectors,
} from 'state/shipments';
import { calculateRoute } from 'state/shipments/effects';
import {
  actions as orderActions,
  actionsByType,
  selectors as orderSelectors,
} from 'state/order';
import {
  constants as addressesConstants,
  methods as addressesMethods,
} from 'utils/addresses/';
import shipmentUtils from 'state/shipments/utils';

import { isEmpty } from 'utils/types';
import keyGenerator from 'utils/keys';

import {
  actions as addressesActions,
  selectors as addressesSelectors,
  types as addressesActionTypes,
} from 'state/addresses';

import { validators as addressesValidators } from 'utils/addresses';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import getCoordinatesFromPOIAddress from 'utils/addresses/methods/getCoordinatesFromPOIAddress';

function* getCoordinatesForPostalCode({ postalCode, countryCode }) {
  try {
    const { data } = yield call(addressApi.searchPostalCode, {
      postalCode,
      countryCode,
    });
    const coordinatesList = yield call(
      addressesMethods.getCoordinatesFromPOIServiceResponse,
      data
    );
    return (coordinatesList || [])[0] || null;
  } catch (e) {
    console.error(e);
    return null;
  }
}

function* handleAddressesRequest({ payload }) {
  const { text, stopType, shipmentKey } = payload;
  yield put(addressesActions.setLoadedAddresses({ addresses: [] }));
  if (!text) {
    yield put(uiActions.toggleLoadingAddresses(false));
    return;
  }

  yield put(
    uiActions.toggleLoadingAddresses({
      shipmentKey,
      stopType,
    })
  );

  try {
    const { data } = yield call(api.searchAddresses, {
      search: encodeURI(text),
      page_size: 50,
      ordering: 'company_name',
    });
    yield put(
      addressesActions.setLoadedAddresses({
        addresses: data.results,
        next: data.next,
        count: data.count,
      })
    );
  } catch (e) {
    console.error(e);
  }
  yield put(uiActions.toggleLoadingAddresses(false));
}

/**
 * Executed when a searched zipcode matches fully with the regex from the shipper country,
 * with this the city is preloaded next to the search, and allows to set it to see the price.
 */
function* handleZipcodeFound({ text, stopType, shipmentKey }) {
  const { country } = yield select(settingsSelectors.getCountrySettings);
  const addressKey = yield call(shipmentUtils.getAddressKey, stopType);

  try {
    const { data: cityData } = yield call(api.getCityName, {
      zipcode: text,
      country,
    });
    const { label, enabled, zipcode, timezone } = cityData;

    yield put(
      shipmentActions.changeValues({
        values: {
          [addressKey]: {
            label,
            enabled,
            zipcode,
            timezone,
            country,
            isZipcode: true,
          },
        },
        shipmentKey,
      })
    );

    if (!cityData.enabled) {
      const errors = {
        [keyGenerator.address[stopType]({
          shipmentKey,
        })]: 'Addresses:error_disabled',
      };
      yield put(errorActions.setError(errors));
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.zipcodeError({ type: 'closed', zipcode: text })
        )
      );
    }
  } catch (err) {
    const errors = {
      [keyGenerator.address[stopType]({ shipmentKey })]:
        'Addresses:error_other',
    };
    yield put(errorActions.setError(errors));
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.zipcodeError({ type: 'invalid', zipcode: text })
      )
    );

    yield put(
      shipmentActions.changeValues({
        values: {
          [addressKey]: {},
        },
        shipmentKey,
      })
    );
  }
}

function* handleZipcodeBehaviour({ payload }) {
  const { text, stopType, shipmentKey } = payload;
  const zipcodeRegExp = yield select(settingsSelectors.getZipcodeRegExp);

  if (zipcodeRegExp.test((text || '').toUpperCase())) {
    yield call(handleZipcodeFound, {
      text: (text || '').toUpperCase(),
      stopType,
      shipmentKey,
    });
  } else {
    const isZipCodeEnabled = yield select(addressesSelectors.isZipCodeEnabled);
    if (isZipCodeEnabled) {
      const addressKey = yield call(shipmentUtils.getAddressKey, stopType);
      shipmentActions.changeValues({
        values: {
          [addressKey]: null,
        },
        shipmentKey,
      });
    }
  }
}

function* handleSearch({ payload }) {
  const { text, stopType, shipmentKey } = payload;
  yield all([
    put(errorActions.cleanErrors()),
    put(warningActions.cleanWarnings()),
  ]);
  const [addressKey, addressRawKey] = yield all([
    call(shipmentUtils.getAddressKey, stopType),
    call(shipmentUtils.getAddressRawKey, stopType),
  ]);

  yield put(
    shipmentActions.changeValues({
      values: {
        [addressRawKey]: text,
        [addressKey]: null,
      },
      shipmentKey,
    })
  );

  if (!text) {
    yield call(initAddresses);
  }
}

function* handleBlur({ payload }) {
  const { shipmentKey, stopType } = payload;

  const [errors] = yield all([
    select(errorsSelectors.getErrors),
    select(warningsSelectors.getWarnings),
  ]);

  const [addressKey, addressRawKey, address] = yield all([
    call(shipmentUtils.getAddressKey, stopType),
    call(shipmentUtils.getAddressRawKey, stopType),
    select(shipmentSelectors.getShipmentAddress, shipmentKey, stopType),
  ]);

  const fieldKey = keyGenerator.address[stopType]({ shipmentKey });

  if (errors[fieldKey] || !address) {
    yield put(
      shipmentActions.changeValues({
        values: {
          [addressRawKey]: '',
          [addressKey]: null,
        },
        shipmentKey,
      })
    );
  }

  yield all([
    put(warningActions.removeWarning([fieldKey])),
    put(errorActions.removeError([fieldKey])),
    call(initAddresses),
  ]);
}

function* handleClean({ payload }) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  yield put(warningActions.cleanWarnings());
  yield put(actionsByType.shipments[orderType].cleanAddress({ ...payload }));
  yield put(actionsByType.ui[orderType].setFetching(false));
  yield put(
    orderActions.setHolidays({
      holidays: null,
      stopType: payload?.stopType,
      shipmentKey: payload?.shipmentKey,
    })
  );
  yield put(orderActions.fetchQuote());
}

function* initAddresses() {
  try {
    const { data } = yield call(api.searchAddresses, {
      page_size: 50,
      ordering: 'company_name',
    });
    if (data?.results) {
      const { results: addresses, next, count } = data;
      yield put(
        addressesActions.setLoadedAddresses({ addresses, next, count })
      );
    }
  } catch (e) {
    console.log(e);
  }
}

function* handleLoadMore({ payload }) {
  const { shipmentKey, stopType } = payload;
  yield put(
    uiActions.toggleLoadingAddresses({
      shipmentKey,
      stopType,
    })
  );
  try {
    const nextLink = yield select(addressesSelectors.getLoadedAddressesNextUrl);
    if (!nextLink) {
      return;
    }
    const { data } = yield call(addressApi.instance.get, nextLink);
    if (data?.results) {
      const currentAddresses = yield select(
        addressesSelectors.getLoadedAddresses
      );
      yield put(
        addressesActions.setLoadedAddresses({
          addresses: [...currentAddresses, ...data.results],
          next: data.next,
          count: data.count,
        })
      );
    }
  } catch (e) {
    console.log(e);
  } finally {
    yield put(uiActions.toggleLoadingAddresses(false));
  }
}

function* handleZipcodeTyped({ payload }) {
  const { zipcode, country } = payload;

  const zipcodeRegExp = yield select(
    settingsSelectors.getZipcodeRegExpByCountry(country)
  );

  let cityData = {};

  yield put(errorActions.cleanErrors());
  yield put(warningActions.cleanWarnings());

  if (!zipcodeRegExp.test(zipcode)) {
    yield put(addressesActions.setCityData({ cityData }));
    return;
  }

  try {
    const { data: response } = yield call(api.getCityName, {
      zipcode,
      country,
    });
    cityData = response;

    if (!cityData.enabled) {
      const errors = { zipcode: 'Addresses:error_disabled' };
      yield put(errorActions.setError(errors));
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.zipcodeError({ type: 'closed', zipcode })
        )
      );
    }
  } catch (err) {
    cityData = {};
    const errors = { zipcode: 'Addresses:error_other' };
    yield put(errorActions.setError(errors));
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.zipcodeError({ type: 'invalid', zipcode })
      )
    );
  }

  yield put(addressesActions.setCityData({ cityData }));
}

function* handleAddressModal({ payload }) {
  const { shipmentKey, stopType, isEditMode } = payload;
  const address = yield select(
    shipmentSelectors.getShipmentAddress,
    shipmentKey,
    stopType
  );
  yield put(
    addressesActions.setCityData({ cityData: address ? { ...address } : {} })
  );
  yield all([
    put(errorActions.cleanErrors()),
    put(warningActions.cleanWarnings()),
  ]);
  yield put(
    uiActions.toggleAddressModal({
      shipmentKey,
      stopType,
      isEditMode,
    })
  );
}

function* handleNewAddressModal() {
  yield put(uiActions.toggleNewAddressModal());
}

function* validateAddress({ formData }) {
  yield all([
    put(errorActions.cleanErrors()),
    put(warningActions.cleanWarnings()),
  ]);

  const { isValid: isFormInputValid, errors } = yield call(
    addressesValidators.validateAddress,
    {
      address: formData,
    }
  );

  let isValid = isFormInputValid;

  yield call(handleZipcodeTyped, { payload: { ...formData } });
  const cityData = yield select(addressesSelectors.getCityData);

  if (isEmpty(cityData)) {
    isValid = false;
    yield put(errorActions.setError({ zipcode: 'Addresses:error_empty' }));
  }

  if (!isEmpty(cityData) && !cityData.enabled) {
    isValid = false;
    yield put(errorActions.setError({ zipcode: 'Addresses:error_disabled' }));
  }

  if (errors && errors.indexOf('contact_phone') >= 0) {
    isValid = false;
    yield put(
      errorActions.setError({ contact_phone: 'Addresses:contact_phone' })
    );
  }
  return { isValid, errors, cityData };
}

function* handleSubmitAddressFormForShipment({ payload }) {
  const { formData, shipmentKey, stopType } = payload;
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const { isValid, errors, cityData } = yield call(validateAddress, {
    formData,
  });

  if (isValid) {
    // close modal and saveAddress
    yield put(
      uiActions.toggleAddressModal({
        shipmentKey,
        stopType,
      })
    );
    const isFrenchAddress = (formData || {}).country === 'FR';

    const addressToSave = {
      ...cityData,
      ...formData,
      city: isFrenchAddress ? formData.city : cityData.city,
      label: isFrenchAddress ? formData.city : cityData.label,
    };

    try {
      const { data: address } = yield call(api.createAddress, {
        address: addressToSave,
      });

      yield put(
        actionsByType.shipments[orderType].changeAddress({
          ...payload,
          address: { ...address },
        })
      );
    } catch (e) {
      console.error(e);
    }
  } else {
    const warnings = errors.reduce(
      (acc, error) => ({ ...acc, [`${error}`]: 'warning' }),
      {}
    );
    yield put(warningActions.setWarning(warnings));
  }
}

function* handleSubmitAddressForm({ payload }) {
  const { formData, onClose } = payload;
  const { isValid, errors, cityData } = yield call(validateAddress, {
    formData,
  });
  const isFrenchAddress = (formData || {}).country === 'FR';

  if (isValid) {
    const addressToSave = {
      ...cityData,
      ...formData,
      city: isFrenchAddress ? formData.city : cityData.city,
      label: isFrenchAddress ? formData.city : cityData.label,
    };
    try {
      if (!isEmpty(addressToSave)) {
        const functionToCall =
          addressToSave && addressToSave.id
            ? api.updateAddress
            : api.createAddress;
        const { data: address } = yield call(functionToCall, {
          address: addressToSave,
        });

        if (isEmpty(address)) {
          throw new TypeError('not address');
        }
        yield put(
          uiActions.setFeedback({
            feedback: `AddressFormModal:actions.${
              addressToSave && addressToSave.id ? 'edit' : 'create'
            }`,
          })
        );
        yield put(addressesActions.setCityData({ cityData: {} }));
        yield call(onClose, { hasToUpdate: true });
        yield call(delay, 10000);
        // add address to list or update it
      } else {
        throw new TypeError('not address');
      }
    } catch (err) {
      yield put(
        uiActions.setFeedback({
          feedback: `AddressFormModal:actions.error_${
            addressToSave && addressToSave.id ? 'edit' : 'create'
          }`,
          success: false,
        })
      );
      yield call(onClose);
      yield call(delay, 10000);
    } finally {
      yield put(uiActions.setFeedback({ feedback: null }));
    }
  } else {
    const warnings = errors.reduce(
      (acc, error) => ({ ...acc, [`${error}`]: 'warning' }),
      {}
    );
    yield put(warningActions.setWarning(warnings));
  }
}

function* handleSubmitDeleteAddress({ payload }) {
  const { addressId, onClose } = payload;
  try {
    yield call(api.deleteAddress, { addressId });

    yield put(
      uiActions.setFeedback({
        feedback: `AddressFormModal:actions.delete`,
      })
    );

    yield call(onClose, { deletedId: addressId });
  } catch (err) {
    yield put(
      uiActions.setFeedback({
        feedback: `AddressFormModal:actions.error_delete`,
        success: false,
      })
    );

    yield call(onClose, { deletedId: false });
  } finally {
    yield call(delay, 10000);
    yield put(uiActions.setFeedback({ feedback: null }));
  }
}

function* handleChangeAddress({ payload }) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  yield put(warningActions.cleanWarnings());
  yield put(orderActions.applyHolidays({ ...payload }));
  yield put(actionsByType.shipments[orderType].changeAddress({ ...payload }));
  yield call(calculateRoute, payload);
}

function* addVenueAddressInfo(venuesResponse) {
  const { features } = venuesResponse;

  const featureCoordinates = features.reduce(
    (acc, feature) => [...acc, getCoordinatesFromPOIAddress(feature)],
    []
  );

  const venueExtraInfoRequests = yield all(
    featureCoordinates.map(({ latitude, longitude }) =>
      call(addressApi.getAddressFromLocation, { latitude, longitude })
    )
  );

  const featuresWithExtraInfo = features.reduce((acc, originalFeature, key) => {
    const [featureExtraInfo] = venueExtraInfoRequests[key].data?.features || [
      null,
    ];

    if (!featureExtraInfo) {
      return [...acc, originalFeature];
    }

    return [
      ...acc,
      {
        ...originalFeature,
        properties: {
          ...featureExtraInfo.properties,
          ...originalFeature.properties,
        },
      },
    ];
  }, []);

  return {
    ...venuesResponse,
    features: featuresWithExtraInfo,
  };
}

function* handleSearchVenuesOfInterest({ payload }) {
  const { text, postalCode, countryCode, city } = payload;
  if (text.length === 0) {
    yield put(addressesActions.setLoadedVenuesOfInterest({ addresses: [] }));
    return;
  }

  try {
    const coordinates = yield call(getCoordinatesForPostalCode, {
      postalCode,
      countryCode,
      city,
    });

    const { data } = yield call(addressApi.searchVenue, {
      text,
      postalCode,
      countryCode,
      coordinates,
    });

    const dataWithInfo = yield call(addVenueAddressInfo, data);

    const formattedAddresses =
      addressesMethods.getAddressesFromPOIServiceResponse(
        dataWithInfo,
        postalCode
      );

    yield put(
      addressesActions.setLoadedVenuesOfInterest({
        addresses: formattedAddresses,
      })
    );
  } catch (e) {
    console.error(e);
  }
  yield put(uiActions.toggleLoadingAddresses(false));
}

function* handleSearchAddressesOfInterest({ payload }) {
  const { text, postalCode, countryCode, city } = payload;
  if (text.length === 0) {
    yield put(addressesActions.setLoadedAddressesOfInterest({ addresses: [] }));
    return;
  }

  try {
    const coordinates = yield call(getCoordinatesForPostalCode, {
      postalCode,
      countryCode,
      city,
    });

    const { data } = yield call(addressApi.searchAddress, {
      text,
      postalCode,
      countryCode,
      coordinates,
    });

    const formattedAddresses =
      addressesMethods.getAddressesFromPOIServiceResponse(data, postalCode);

    const completeAddresses = formattedAddresses.filter(
      (address) => address.isComplete
    );
    const incompleteAddresses = formattedAddresses.filter(
      (address) => !address.isComplete
    );

    if (incompleteAddresses.length > 0) {
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.addresses.detectedIncompleteAddressSuggestions({
            totalNumberOfAddresses: (formattedAddresses || []).length,
            numberOfIncompleteAddresses: incompleteAddresses.length,
            suggestionType:
              addressesConstants.POI_ADDRESS_CONSTANTS.address_type,
          })
        )
      );
    }

    yield put(
      addressesActions.setLoadedAddressesOfInterest({
        addresses: completeAddresses || data,
      })
    );
  } catch (e) {
    console.error(e);
  }
  yield put(uiActions.toggleLoadingAddresses(false));
}

function* handleStructuredSearchForAddress({ payload }) {
  const { text, postalCode, countryCode } = payload;
  if (text.length === 0) return;
  const { data } = yield call(addressApi.parseAddressSearch, {
    text,
  });

  const params =
    addressesMethods.getPOIServiceParamsFromAddressParserResponse(data);

  const { data: searchResponseData } = yield call(
    addressApi.searchStructuredAddress,
    {
      params,
      postalCode,
      countryCode,
    }
  );

  const formattedAddresses =
    addressesMethods.getAddressesFromPOIServiceResponse(
      searchResponseData,
      postalCode
    );

  yield put(
    addressesActions.setLoadedAddressesOfInterest({
      addresses: formattedAddresses || data,
    })
  );
}

export default function* () {
  yield takeLatest(addressesActionTypes.searchAddresses$, handleSearch);
  yield takeLatest(
    addressesActionTypes.searchAddresses$,
    handleZipcodeBehaviour
  );
  yield throttle(
    500,
    addressesActionTypes.searchAddresses$,
    handleAddressesRequest
  );

  yield takeLatest(addressesActionTypes.blurAddresses$, handleBlur);
  yield takeLatest(addressesActionTypes.fetchAddresses$, initAddresses);
  yield takeLatest(addressesActionTypes.loadMoreAddresses$, handleLoadMore);
  yield takeLatest(addressesActionTypes.cleanAddress$, handleClean);
  yield takeLatest(addressesActionTypes.zipcodeTyped$, handleZipcodeTyped);

  yield takeLatest(
    addressesActionTypes.closeCreateAddress$,
    handleAddressModal
  );
  yield takeLatest(addressesActionTypes.openAddressModal$, handleAddressModal);
  yield takeLatest(
    addressesActionTypes.openNewAddressModal$,
    handleNewAddressModal
  );

  yield takeLatest(
    addressesActionTypes.submitCreateAddress$,
    handleSubmitAddressForm
  );
  yield takeLatest(
    addressesActionTypes.submitDeleteAddress$,
    handleSubmitDeleteAddress
  );

  yield takeLatest(
    addressesActionTypes.submitCreateAddressForShipment$,
    handleSubmitAddressFormForShipment
  );
  yield takeLatest(addressesActionTypes.changeAddress$, handleChangeAddress);
  yield takeLatest(
    addressesActionTypes.searchVenuesOfInterest$,
    handleSearchVenuesOfInterest
  );
  yield takeLatest(
    addressesActionTypes.searchAddressesOfInterest$,
    handleSearchAddressesOfInterest
  );
  yield takeLatest(
    addressesActionTypes.searchStructuredAddress$,
    handleStructuredSearchForAddress
  );
}

export {
  initAddresses,
  getCoordinatesForPostalCode,
  handleSearch,
  addVenueAddressInfo,
  handleChangeAddress,
  handleClean,
};
